import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["minRows", "maxRows"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * Реализация основана на TextareaAutosize из Material UI
 * https://github.com/mui-org/material-ui/blob/21dba852b1a48cf427326884815d82b8eeca795c/packages/mui-base/src/TextareaAutosize/TextareaAutosize.js
 */
import * as React from 'react';
import { useForkRef, useIsomorphicLayoutEffect } from '@cian/react-utils';
import { clsx } from 'clsx';
import { ownerWindow, debounce } from '../../../../internal/utils';
import styles from './styles.module.css';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const MAX_RERENDERS = 20;

/**
 * Компонент Textarea который подгоняет высоту под количество введенного текста
 */
export const TextareaAutosize = /*#__PURE__*/React.forwardRef(function Textarea(props, ref) {
  const {
      minRows,
      maxRows
    } = props,
    nativeProps = _objectWithoutProperties(props, _excluded);
  const {
    value,
    className
  } = props;
  const {
    current: isControlled
  } = React.useRef(value != null);
  const shadowRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const handleRef = useForkRef(inputRef, ref);
  const renders = React.useRef(0);
  const [state, setState] = React.useState({});
  const syncHeight = React.useCallback(() => {
    const input = inputRef.current;
    const shadowInput = shadowRef.current;
    if (!input || !shadowInput) {
      return;
    }
    const containerWindow = ownerWindow(input);
    const computedStyle = containerWindow.getComputedStyle(input);

    /**
     * If input's width is shrunk and it's not visible, don't sync height.
     *
     * https://github.com/mui-org/material-ui/pull/23273
     */
    if (computedStyle.width === '0px') {
      return;
    }
    shadowInput.style.width = computedStyle.width;
    shadowInput.value = input.value || props.placeholder || 'x';
    if (shadowInput.value.slice(-1) === '\n') {
      /**
       * Certain fonts which overflow the line height will cause the textarea
       * to report a different scrollHeight depending on whether the last line
       * is empty. Make it non-empty to avoid this issue.
       *
       * https://github.com/mui-org/material-ui/pull/20575
       */
      shadowInput.value += ' ';
    }
    const {
      boxSizing
    } = computedStyle;
    const padding = getStyleIntValue(computedStyle, 'paddingBottom') + getStyleIntValue(computedStyle, 'paddingTop');
    const border = getStyleIntValue(computedStyle, 'borderBottomWidth') + getStyleIntValue(computedStyle, 'borderTopWidth');

    // The height of the inner content
    const innerHeight = shadowInput.scrollHeight;

    // Measure height of a textarea with a single row
    shadowInput.value = 'x';
    const singleRowHeight = shadowInput.scrollHeight;

    // The height of the outer content
    let outerHeight = innerHeight;
    if (minRows) {
      outerHeight = Math.max(Number(minRows) * singleRowHeight, outerHeight);
    }
    if (maxRows) {
      outerHeight = Math.min(Number(maxRows) * singleRowHeight, outerHeight);
    }
    outerHeight = Math.max(outerHeight, singleRowHeight);

    // Take the box sizing into account for applying this value as a style.
    const outerHeightStyle = outerHeight + (boxSizing === 'border-box' ? padding + border : 0);
    // Need a large enough different to allow scrolling.
    // This prevents infinite rendering loop.
    const overflow = Math.abs(outerHeight - innerHeight) <= 1;
    setState(prevState => {
      /**
       * Need a large enough difference to update the height.
       * This prevents infinite rendering loop.
       *
       * https://github.com/mui-org/material-ui/issues/17672
       */
      if (renders.current < MAX_RERENDERS && (
      // Has any changes
      outerHeightStyle > 0 && Math.abs((prevState.outerHeightStyle || 0) - outerHeightStyle) > 1 || prevState.overflow !== overflow)) {
        renders.current += 1;
        return {
          overflow,
          outerHeightStyle
        };
      }
      if (process.env.NODE_ENV !== 'production') {
        if (renders.current >= MAX_RERENDERS) {
          console.error(['UI-KIT: Too many re-renders. The layout is unstable.', 'TextareaAutosize limits the number of renders to prevent an infinite loop.'].join('\n'));
        }
      }
      return prevState;
    });
  }, [maxRows, minRows, props.placeholder]);
  React.useEffect(() => {
    const handleResize = debounce(() => {
      renders.current = 0;
      syncHeight();
    });
    const containerWindow = ownerWindow(inputRef.current);
    containerWindow.addEventListener('resize', handleResize);
    let resizeObserver;

    // Нужен для синхронизации размеров, когда изменяется ширина
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(inputRef.current);
    }
    return () => {
      handleResize.cancel();
      containerWindow.removeEventListener('resize', handleResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [syncHeight]);
  useIsomorphicLayoutEffect(() => {
    syncHeight();
  });
  React.useEffect(() => {
    renders.current = 0;
  }, [value]);
  const handleChange = e => {
    renders.current = 0;
    if (!isControlled) {
      syncHeight();
    }
    if (props.onChange) {
      props.onChange(e);
    }
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("textarea", _objectSpread(_objectSpread({}, nativeProps), {}, {
      ref: handleRef,
      onChange: handleChange,
      rows: minRows,
      style: _objectSpread({
        height: state.outerHeightStyle,
        overflow: state.overflow ? 'hidden' : undefined
      }, props.style)
    })), /*#__PURE__*/_jsx("textarea", {
      "aria-hidden": true,
      ref: shadowRef,
      readOnly: true,
      tabIndex: -1,
      className: clsx(className, styles['shadow']),
      style: props.style
    })]
  });
});
function getStyleIntValue(computedStyle, property) {
  return parseInt(computedStyle[property], 10) || 0;
}